/**
 * Derived game state and the reducer that produces it.
 *
 * Never mutate this from gameplay code — go through `GameSession.emit` so the
 * event log stays the sole author of state (docs/04 §0).
 */

import { createDiceState, type DiceState } from './dice.js';
import type { GameEvent } from './events.js';
import { adjust, createNpcMemory, remember, type NpcMemory } from './npc.js';
import type {
  CardId,
  ClassId,
  EasterEggId,
  FactId,
  ItemId,
  MonsterId,
  NpcId,
  QuestId,
  RoomId,
  Stats,
  Traits,
} from './types.js';
import { STATS } from './types.js';

export interface PlayerState {
  name: string;
  classId: ClassId;
  level: number;
  xp: number;
  hp: number;
  maxHp: number;
  stats: Stats;
  traits: Traits;
  gold: number;
  /** Behaviour counters that unlock hidden classes (GDD §6). */
  counters: Record<string, number>;
  titles: string[];
  hiddenClasses: string[];
}

export interface InventoryState {
  items: ItemId[];
  equipped: Partial<Record<'weapon' | 'armor' | 'trinket', ItemId>>;
}

export interface DeckState {
  /** Cards owned. A card can be present as a transformed variant (`id+overlay`). */
  cards: CardId[];
  drawPile: CardId[];
  hand: CardId[];
  discard: CardId[];
  removed: CardId[];
}

export interface WorldState {
  tick: number;
  /** Aggregated counters that survive snapshot folding. */
  aggregates: Record<string, number>;
  /** Derived booleans. Do not write directly; add a selector instead (docs/04 §1.4). */
  flags: Set<string>;
  knownFacts: Set<FactId>;
  discoveredSecrets: Set<string>;
  foundEasterEggs: Set<EasterEggId>;
  clearedRooms: Set<RoomId>;
  killedMonsters: Record<MonsterId, number>;
  sparedMonsters: Set<MonsterId>;
  activeQuests: Set<QuestId>;
  completedQuests: Set<QuestId>;
}

export interface ChronicleEntry {
  readonly chapter: number;
  readonly title: string;
  readonly body: string;
  readonly tick: number;
  readonly eventSeq: number;
}

export interface GameState {
  runSeed: string;
  player: PlayerState;
  inventory: InventoryState;
  deck: DeckState;
  dice: DiceState;
  world: WorldState;
  npcs: Record<NpcId, NpcMemory>;
  chronicle: ChronicleEntry[];
  currentRoom: RoomId | null;
  /** null while the run is live. */
  ended: { reason: 'victory' | 'death' | 'abandoned'; tick: number } | null;
}

export const BASE_STATS: Readonly<Record<ClassId, Stats>> = {
  warrior: { might: 4, agility: 2, wits: 1, spirit: 2 },
  rogue: { might: 2, agility: 4, wits: 3, spirit: 1 },
  mage: { might: 1, agility: 2, wits: 4, spirit: 3 },
  priest: { might: 2, agility: 1, wits: 2, spirit: 4 },
};

export const BASE_HP: Readonly<Record<ClassId, number>> = {
  warrior: 30,
  rogue: 24,
  // 20 이었다. 마법사는 체력이 가장 낮으면서 주력 전술이 2라운드짜리 준비였고,
  // 한 방 피해조차 사제의 invoke 보다 낮았다 — 유리대포가 아니라 그냥 유리였다.
  // 측정에서 마법사만 사망률 98% (다른 클래스 72~78%) 로 튀었다.
  mage: 24,
  priest: 26,
};

/** 레벨당 최대 체력 증가분. */
export const HP_PER_LEVEL = 4;

/**
 * 휴식 (GDD §12-1). 굴린 눈이 회복을 정한다 — 1 은 악몽(1/4 · 운명 빚), 6 은 깊은 잠(전부).
 * 콘텐츠가 `rest` 를 단 방에서만, 방마다 한 판에 한 번. 수치는 여기 한 곳에만 둔다.
 */
export const REST_HEAL_RATIO: Readonly<Record<'nightmare' | 'shallow' | 'deep', number>> = {
  nightmare: 0.25,
  shallow: 0.5,
  deep: 1,
};
/** 악몽이 남기는 운명 빚. */
export const REST_NIGHTMARE_FATE_DEBT = 6;

/** 방이 "여기서 잤다"를 기억하는 깃발. 방마다 한 판에 한 번(GDD §12-1). */
export function restedFlag(roomId: RoomId): string {
  return `rested:${roomId}`;
}

/** 휴식 굴림 한 번의 결과 — 세션이 굴리고 리듀서는 `healed` 만 쓴다. */
export function restOutcome(face: number, hp: number, maxHp: number): {
  kind: 'nightmare' | 'shallow' | 'deep';
  healed: number;
  fateDebt: number;
} {
  const kind = face <= 1 ? 'nightmare' : face >= 6 ? 'deep' : 'shallow';
  const want = Math.ceil(maxHp * REST_HEAL_RATIO[kind]);
  return {
    kind,
    healed: Math.max(0, Math.min(maxHp - hp, want)),
    fateDebt: kind === 'nightmare' ? REST_NIGHTMARE_FATE_DEBT : 0,
  };
}

/**
 * 다음 레벨까지 필요한 경험치.
 *
 * 설계 기준: **던전을 한 번 다 비우면 보스를 상대할 레벨(Lv4)이 된다.**
 * 한 판에서 버는 경험치는 실측 약 104 이고, Lv4 누적 비용은 105 다.
 * 누적: Lv2 25 · Lv3 60 · Lv4 105 · Lv5 160.
 * Lv5 는 한 판으로는 닿지 않는다 — 그건 잘 풀린 판의 보상이다.
 *
 * 처음엔 `level * 50` 이라 Lv5 까지 500 이 필요했다. 플레이어는 언제나 Lv2 로
 * 보스를 만났고 보스는 Lv5 기준이었다 — 도달할 수 없는 레벨을 전제한 난이도였다.
 *
 * 그 뒤 `40 + (level-1)*15` 로 한 번 고쳤는데, 그때 근거로 삼은 "한 판 경험치
 * 108~183" 은 **방 재진입 파밍이 섞인 값**이었다 (같은 방을 오가면 인카운터가
 * 다시 열렸다). 파밍을 막고 다시 재니 실제 획득량은 104 였다. 측정이 틀리면
 * 그 위에 세운 튜닝도 같이 틀린다 — 절대 규칙 12가 있는 이유다.
 *
 * 레벨업은 체력을 가득 채우므로, 커브를 낮추는 것은 회복 빈도를 올리는 것이기도
 * 하다. (2026-09-28 부터는 휴식 지점 두 곳도 있다 — GDD §12-1.)
 */
export function xpForNextLevel(level: number): number {
  return 25 + (level - 1) * 10;
}

/**
 * 레벨당 능력치 성장 — GDD §7.
 *
 * 이게 없던 동안 레벨업은 최대 체력 +4 만 줬다. 그래서 Lv5 플레이어가 Lv1 과
 * 정확히 같은 피해를 냈고, 몬스터 방어력은 그대로라 레벨이 오를수록 전투가
 * 길어지기만 했다 — 더 오래 맞는다는 뜻이다. 성장이 체력에만 붙으면
 * 진행이 아니라 지연이 된다.
 *
 * 성장은 클래스의 정체성을 따라간다. 전사는 힘, 도적은 민첩, 마법사는 지혜,
 * 사제는 정신. 부차 능력치는 2레벨에 한 번 올라서, 클래스가 시간이 지나도
 * 자기 색을 잃지 않는다.
 */
export const STAT_GROWTH: Readonly<Record<ClassId, Readonly<Partial<Stats>>>> = {
  warrior: { might: 1, spirit: 0.5 },
  rogue: { agility: 1, wits: 0.5 },
  mage: { wits: 1, spirit: 0.5 },
  priest: { spirit: 1, wits: 0.5 },
};

/** 해당 레벨에서의 능력치. 레벨업 처리와 시뮬레이터가 같은 계산을 쓰게 하는 단일 출처. */
export function statsAtLevel(classId: ClassId, level: number): Stats {
  const out = { ...BASE_STATS[classId] };
  const growth = STAT_GROWTH[classId];
  for (const key of STATS) {
    // 0.5 성장은 두 레벨에 1 오른다. 소수점 능력치는 존재하지 않는다.
    out[key] += Math.floor((growth[key] ?? 0) * (level - 1));
  }
  return out;
}

export function createGameState(runSeed: string, classId: ClassId, name = 'Wanderer'): GameState {
  return {
    runSeed,
    player: {
      name,
      classId,
      level: 1,
      xp: 0,
      hp: BASE_HP[classId],
      maxHp: BASE_HP[classId],
      stats: { ...BASE_STATS[classId] },
      traits: {},
      gold: 10,
      counters: {},
      titles: [],
      hiddenClasses: [],
    },
    inventory: { items: [], equipped: {} },
    deck: { cards: [], drawPile: [], hand: [], discard: [], removed: [] },
    dice: createDiceState(),
    world: {
      tick: 0,
      aggregates: {},
      flags: new Set(),
      knownFacts: new Set(),
      discoveredSecrets: new Set(),
      foundEasterEggs: new Set(),
      clearedRooms: new Set(),
      killedMonsters: {},
      sparedMonsters: new Set(),
      activeQuests: new Set(),
      completedQuests: new Set(),
    },
    npcs: {},
    chronicle: [],
    currentRoom: null,
    ended: null,
  };
}

export function npcMemory(state: GameState, npcId: NpcId): NpcMemory {
  return (state.npcs[npcId] ??= createNpcMemory(npcId));
}

function bump(state: GameState, key: string, by = 1): void {
  state.world.aggregates[key] = (state.world.aggregates[key] ?? 0) + by;
}

function bumpCounter(state: GameState, key: string, by = 1): void {
  state.player.counters[key] = (state.player.counters[key] ?? 0) + by;
}

function addTrait(state: GameState, trait: keyof Traits, by = 1): void {
  state.player.traits[trait] = (state.player.traits[trait] ?? 0) + by;
}

/** Memory weight by event type: how vividly an NPC retains this (npc.ts §1). */
const MEMORY_WEIGHT: Record<string, number> = {
  NPC_SAVED: 100,
  NPC_KILLED: 100,
  NPC_BETRAYED: 95,
  PROMISE_BROKEN: 90,
  PROMISE_KEPT: 75,
  NPC_ROBBED: 70,
  NPC_HELPED: 55,
  NPC_LIED_TO: 60,
  NPC_PERSUADED: 35,
  NPC_MET: 25,
  RUMOR_HEARD: 20,
};

const num = (v: unknown, fallback = 0): number => (typeof v === 'number' ? v : fallback);
const str = (v: unknown, fallback = ''): string => (typeof v === 'string' ? v : fallback);

/**
 * Apply one event to state. Pure with respect to the event: given the same state
 * and event it always produces the same mutation, which is what makes replay
 * from a log reproduce a run exactly.
 */
export function applyEvent(state: GameState, e: GameEvent): void {
  state.world.tick = Math.max(state.world.tick, e.t);

  switch (e.type) {
    case 'RUN_STARTED':
      bump(state, 'runs');
      break;

    case 'RUN_ENDED': {
      const reason = str(e.data?.['reason'], 'abandoned');
      state.ended = {
        reason: reason === 'victory' || reason === 'death' ? reason : 'abandoned',
        tick: e.t,
      };
      break;
    }

    case 'CLASS_CHOSEN':
      state.player.classId = str(e.data?.['classId'], state.player.classId) as ClassId;
      break;

    case 'DICE_ROLLED':
      bump(state, 'rolls');
      bump(state, `rolls.face.${num(e.data?.['face'])}`);
      break;

    case 'DICE_MANIPULATED':
      bump(state, 'manipulations');
      bump(state, `manipulations.${str(e.data?.['kind'], 'unknown')}`);
      bumpCounter(state, 'fateManipulations');
      break;

    case 'FATE_DEBT_THRESHOLD':
      bump(state, 'fateEvents');
      state.world.flags.add('fateDebtAwakened');
      break;

    case 'FATE_CHAIN_TRIGGERED':
      bump(state, 'fateChains');
      addTrait(state, 'lucky');
      break;

    case 'BAD_LUCK_TRIGGERED':
      bump(state, 'badLuckStreaks');
      break;

    case 'MOVED':
      bump(state, 'steps', num(e.data?.['distance'], 1));
      break;

    case 'MOVE_FORFEITED':
      bump(state, 'movesForfeited');
      break;

    case 'ROOM_ENTERED':
      if (e.room) state.currentRoom = e.room;
      bump(state, 'roomsEntered');
      break;

    case 'ROOM_CLEARED':
      if (e.room) state.world.clearedRooms.add(e.room);
      bump(state, 'roomsCleared');
      break;

    case 'RESTED': {
      // 회복량은 이벤트에 적힌 값 그대로 — 굴림은 세션이 했고, 리플레이는 결과만 다시 쓴다.
      const healed = Math.max(0, num(e.data?.['healed'], 0));
      state.player.hp = Math.min(state.player.maxHp, state.player.hp + healed);
      if (e.room) state.world.flags.add(restedFlag(e.room));
      bump(state, 'rests');
      break;
    }

    case 'SECRET_DISCOVERED':
      state.world.discoveredSecrets.add(str(e.data?.['secretId'], `secret@${e.seq}`));
      addTrait(state, 'curious', 2);
      bump(state, 'secrets');
      break;

    case 'CHOICE_MADE': {
      bump(state, 'choices');
      const trait = str(e.data?.['trait']);
      if (trait) addTrait(state, trait as keyof Traits);
      break;
    }

    case 'CHOICE_TIMED_OUT':
      bump(state, 'choicesTimedOut');
      // GDD §17: waiting instead of choosing is itself an action.
      state.world.flags.add('hasWaited');
      break;

    case 'COMBAT_WON':
      bump(state, 'combatsWon');
      break;

    case 'COMBAT_FLED':
      bump(state, 'combatsFled');
      addTrait(state, 'coward');
      break;

    case 'MONSTER_KILLED': {
      const id = str(e.target);
      if (id) state.world.killedMonsters[id] = (state.world.killedMonsters[id] ?? 0) + 1;
      bump(state, 'monstersKilled');
      addTrait(state, 'brave');
      break;
    }

    case 'MONSTER_SPARED':
      if (e.target) state.world.sparedMonsters.add(e.target);
      bump(state, 'monstersSpared');
      addTrait(state, 'kind');
      break;

    case 'BOSS_KILLED':
      bump(state, 'bossesKilled');
      addTrait(state, 'brave', 3);
      state.world.flags.add('bossDefeated');
      break;

    case 'BOSS_SPARED':
      bump(state, 'bossesSpared');
      addTrait(state, 'kind', 3);
      if (e.target) state.world.sparedMonsters.add(e.target);
      state.world.flags.add('bossSpared');
      break;

    case 'BOSS_RECRUITED':
      bump(state, 'bossesRecruited');
      state.world.flags.add('bossAlly');
      break;

    case 'PLAYER_DIED':
      bump(state, 'deaths');
      bumpCounter(state, 'deaths');
      state.player.hp = 0;
      break;

    case 'NPC_MET': {
      const npc = npcMemory(state, str(e.target));
      npc.metCount += 1;
      npc.lastSeenTick = e.t;
      bump(state, 'npcsMet');
      break;
    }

    case 'NPC_SPOKE': {
      const npc = npcMemory(state, str(e.target));
      npc.lastSeenTick = e.t;
      break;
    }

    case 'NPC_HELPED': {
      const npc = npcMemory(state, str(e.target));
      adjust(npc, { trust: +12, liking: +10, debt: +1 });
      addTrait(state, 'kind');
      bumpCounter(state, 'helped');
      break;
    }

    case 'NPC_SAVED': {
      const npc = npcMemory(state, str(e.target));
      adjust(npc, { trust: +35, liking: +30, respect: +20, debt: +3 });
      addTrait(state, 'kind', 3);
      bumpCounter(state, 'saved');
      bump(state, 'npcsSaved');
      break;
    }

    case 'NPC_KILLED': {
      const npc = npcMemory(state, str(e.target));
      npc.alive = false;
      npc.diedAtTick = e.t;
      adjust(npc, { trust: -100, liking: -100, resentment: +100 });
      addTrait(state, 'cruel', 3);
      bumpCounter(state, 'killedNpcs');
      bump(state, 'npcsKilled');
      break;
    }

    case 'NPC_BETRAYED': {
      const npc = npcMemory(state, str(e.target));
      adjust(npc, { trust: -60, liking: -40, resentment: +70 });
      addTrait(state, 'deceptive', 3);
      bumpCounter(state, 'betrayals');
      break;
    }

    case 'NPC_ROBBED': {
      const npc = npcMemory(state, str(e.target));
      adjust(npc, { trust: -30, liking: -20, resentment: +35 });
      addTrait(state, 'greedy', 2);
      bumpCounter(state, 'thefts');
      break;
    }

    case 'NPC_PERSUADED': {
      const npc = npcMemory(state, str(e.target));
      adjust(npc, { respect: +8 });
      bumpCounter(state, 'persuasions');
      break;
    }

    case 'NPC_LIED_TO': {
      const npc = npcMemory(state, str(e.target));
      adjust(npc, { trust: -10 });
      addTrait(state, 'deceptive', 2);
      bumpCounter(state, 'lies');
      break;
    }

    case 'PROMISE_MADE': {
      const npc = npcMemory(state, str(e.target));
      npc.promises.push({
        id: str(e.data?.['promiseId'], `promise@${e.seq}`),
        description: str(e.data?.['description']),
        dueTick: num(e.data?.['dueTick'], e.t + 10),
        ...(typeof e.data?.['dueRealMs'] === 'number'
          ? { dueRealMs: e.data['dueRealMs'] as number }
          : {}),
        status: 'open',
      });
      break;
    }

    case 'PROMISE_KEPT': {
      const npc = npcMemory(state, str(e.target));
      const p = npc.promises.find((x) => x.id === str(e.data?.['promiseId']));
      if (p) p.status = 'kept';
      adjust(npc, { trust: +20, liking: +15, respect: +10 });
      addTrait(state, 'honest', 2);
      bumpCounter(state, 'promisesKept');
      break;
    }

    case 'PROMISE_BROKEN': {
      const npc = npcMemory(state, str(e.target));
      const p = npc.promises.find((x) => x.id === str(e.data?.['promiseId']));
      if (p) p.status = 'broken';
      adjust(npc, { trust: -25, liking: -15, resentment: +30 });
      addTrait(state, 'deceptive');
      bumpCounter(state, 'promisesBroken');
      break;
    }

    case 'ITEM_GAINED': {
      const id = str(e.data?.['itemId'], str(e.target));
      if (id) state.inventory.items.push(id);
      bump(state, 'itemsGained');
      break;
    }

    case 'ITEM_LOST': {
      const id = str(e.data?.['itemId'], str(e.target));
      const idx = state.inventory.items.indexOf(id);
      if (idx >= 0) state.inventory.items.splice(idx, 1);
      break;
    }

    case 'ITEM_USED':
      bump(state, 'itemsUsed');
      break;

    case 'CARD_DRAWN': {
      const id = str(e.data?.['cardId']);
      const from = state.deck.drawPile.indexOf(id);
      if (from >= 0) state.deck.drawPile.splice(from, 1);
      if (id) state.deck.hand.push(id);
      break;
    }

    case 'CARD_PLAYED': {
      const id = str(e.data?.['cardId']);
      const idx = state.deck.hand.indexOf(id);
      if (idx >= 0) state.deck.hand.splice(idx, 1);
      if (id) state.deck.discard.push(id);
      bump(state, 'cardsPlayed');
      break;
    }

    case 'CARD_TRANSFORMED': {
      // Transformation swaps the base id for `base+overlay` everywhere it lives.
      const from = str(e.data?.['from']);
      const to = str(e.data?.['to']);
      const swap = (arr: string[]): void => {
        const i = arr.indexOf(from);
        if (i >= 0) arr[i] = to;
      };
      swap(state.deck.cards);
      swap(state.deck.drawPile);
      swap(state.deck.hand);
      swap(state.deck.discard);
      bump(state, 'cardsTransformed');
      break;
    }

    case 'CARD_REMOVED': {
      const id = str(e.data?.['cardId']);
      for (const pile of [
        state.deck.cards,
        state.deck.drawPile,
        state.deck.hand,
        state.deck.discard,
      ]) {
        const i = pile.indexOf(id);
        if (i >= 0) pile.splice(i, 1);
      }
      if (id) state.deck.removed.push(id);
      break;
    }

    case 'FACT_LEARNED':
      state.world.knownFacts.add(str(e.data?.['factId']));
      bump(state, 'factsLearned');
      break;

    case 'RUMOR_HEARD':
      bump(state, 'rumorsHeard');
      break;

    case 'EASTER_EGG_FOUND':
      state.world.foundEasterEggs.add(str(e.data?.['eggId'], str(e.target)));
      bump(state, 'easterEggs');
      addTrait(state, 'curious', 3);
      break;

    case 'TITLE_EARNED': {
      const title = str(e.data?.['title']);
      if (title && !state.player.titles.includes(title)) state.player.titles.push(title);
      break;
    }

    case 'CHRONICLE_CHAPTER':
      state.chronicle.push({
        chapter: num(e.data?.['chapter'], state.chronicle.length + 1),
        title: str(e.data?.['title']),
        body: str(e.data?.['body']),
        tick: e.t,
        eventSeq: e.seq,
      });
      break;

    case 'ENCOUNTER_STARTED':
    case 'COMBAT_STARTED':
    case 'ANALYTICS':
      break;
  }

  // Memories attach after the primary mutation so trust deltas are already applied.
  if (e.target && MEMORY_WEIGHT[e.type] !== undefined && state.npcs[e.target]) {
    const npc = state.npcs[e.target]!;
    remember(npc, {
      id: `mem@${e.seq}`,
      eventType: e.type,
      summary: str(e.data?.['summary'], e.type),
      tick: e.t,
      ...(e.rollRef !== undefined ? { rollRef: e.rollRef } : {}),
      weight: MEMORY_WEIGHT[e.type]!,
      valence: num(e.data?.['valence'], 0),
    });
  }
}

/** Replay a whole log into a fresh state. The determinism test relies on this. */
export function replay(
  runSeed: string,
  classId: ClassId,
  events: readonly GameEvent[],
  name = 'Wanderer',
): GameState {
  const state = createGameState(runSeed, classId, name);
  for (const e of events) applyEvent(state, e);
  return state;
}

// ---------------------------------------------------------------------------
// Hidden class unlocks (GDD §6)
// ---------------------------------------------------------------------------

export interface HiddenClassRule {
  readonly id: string;
  readonly label: string;
  readonly counter: string;
  readonly threshold: number;
}

/**
 * Thresholds are scaled down from the GDD's illustrative numbers (100 persuasions,
 * 100 deaths) because those were written for a full live game. At vertical-slice
 * length nobody would ever see one, and an unlock nobody sees teaches nothing.
 * Raise these in GOD-3 once run length supports them.
 */
export const HIDDEN_CLASS_RULES: readonly HiddenClassRule[] = [
  { id: 'diplomat', label: 'Diplomat', counter: 'persuasions', threshold: 12 },
  { id: 'masterThief', label: 'Master Thief', counter: 'thefts', threshold: 10 },
  { id: 'deathWalker', label: 'Death Walker', counter: 'deaths', threshold: 10 },
  { id: 'fateWalker', label: 'Fate Walker', counter: 'fateManipulations', threshold: 25 },
  { id: 'necromancer', label: 'Necromancer', counter: 'killedNpcs', threshold: 6 },
  { id: 'paladin', label: 'Paladin', counter: 'saved', threshold: 8 },
  { id: 'assassin', label: 'Assassin', counter: 'betrayals', threshold: 5 },
  { id: 'bard', label: 'Bard', counter: 'rumorsShared', threshold: 8 },
];

/** Returns newly unlocked hidden classes and records them on the player. */
export function checkHiddenClasses(state: GameState): HiddenClassRule[] {
  const unlocked: HiddenClassRule[] = [];
  for (const rule of HIDDEN_CLASS_RULES) {
    if (state.player.hiddenClasses.includes(rule.id)) continue;
    if ((state.player.counters[rule.counter] ?? 0) >= rule.threshold) {
      state.player.hiddenClasses.push(rule.id);
      unlocked.push(rule);
    }
  }
  return unlocked;
}

export function dominantTrait(state: GameState): keyof Traits | null {
  let best: keyof Traits | null = null;
  let bestVal = 0;
  for (const [k, v] of Object.entries(state.player.traits)) {
    if ((v ?? 0) > bestVal) {
      bestVal = v ?? 0;
      best = k as keyof Traits;
    }
  }
  return best;
}
